import styled from '@emotion/styled';
import { Project } from '@gameonly/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { WhiteButton } from '../ui/Button';
import LoadingButton from '../ui/LoadingButton';
import { useProject } from './context/useProject';

import { CheckCircle } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Completion from './Completion';
import { useValidationContext } from './context/useValidation';

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTable = styled(Table)`
  margin-top: 16px;
  font-size: 0.9rem;
  th,
  td {
    padding: 4px 8px;
    border: 1px solid black;
  }
`;

const FinalisationSection = ({ project }: { project: Project }) => {
  const { showValidation } = useValidationContext();
  const { t } = useTranslation(['project']);
  const { finalizeProject, loadingFinalizeProject } = useProject(
    project._id.toString(),
  );
  const [openFinalize, setOpenFinalize] = useState<boolean>(false);

  const isFinalized = !!project.finalizedAt;

  const handleFinalizeProject = async () => {
    finalizeProject(true);
  };

  const handleUnfinalizeProject = async () => {
    finalizeProject(false);
  };

  return (
    <>
      <FinalizeProjectModal
        open={openFinalize}
        onClose={() => setOpenFinalize(false)}
        loading={loadingFinalizeProject}
        project={project}
        onSave={handleFinalizeProject}
      />
      {!isFinalized ? (
        <>
          <Header>
            <Completion project={project} onClick={showValidation} />
            <Button
              onClick={() => setOpenFinalize(true)}
              color="primary"
              startIcon={<DoneAllIcon />}
            >
              {t('project:finalized-section.validate_button')}
            </Button>
          </Header>
        </>
      ) : (
        <>
          <Header>
            <Title>
              <CheckCircleIcon fontSize="medium" color={'inherit'} />
              <p className="h4b">
                {t('project:finalized-section.validated-title')}
              </p>
            </Title>
            <LoadingButton
              onClick={handleUnfinalizeProject}
              color="primary"
              startIcon={<LockOpenOutlinedIcon />}
              loading={loadingFinalizeProject}
            >
              {t('project:finalized-section.reopen-button')}
            </LoadingButton>
          </Header>
        </>
      )}
    </>
  );
};

const SideInfoContainer = styled(Paper)`
  padding: 16px;
  background-color: var(--color-primary-light);
  border: 1px solid #e0e0e0;
  margin-top: 16px;
`;

interface FinalizeProjectModalProps {
  open: boolean;
  project: Project;
  onClose: () => void;
  onSave: () => void;
  loading: boolean;
}

const FinalizeProjectModal = ({
  open,
  onClose,
  onSave,
  loading,
  project,
}: FinalizeProjectModalProps) => {
  const { t } = useTranslation(['project', 'common']);

  const handleConfirm = async () => {
    await onSave();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t('project:finalized-section.confirmation-modal.title')}
      </DialogTitle>
      <DialogContent>
        <p className="hxr">
          {t('project:finalized-section.confirmation-modal.info')}
        </p>
        <SideInfoContainer>
          <p className="hxr">
            {t('project:finalized-section.confirmation-modal.table-intro')}
          </p>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('project:sector')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('project:status')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {t('project:completion-rate')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project.sectors.map((sector, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {sector.information?.name || t('common:unknown')}
                  </TableCell>
                  <TableCell>
                    {sector.completionRate === 100 ? (
                      <CheckCircle color="success" />
                    ) : (
                      <WarningAmberOutlinedIcon color="warning" />
                    )}
                  </TableCell>
                  <TableCell>{sector.completionRate || 0}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </SideInfoContainer>
      </DialogContent>
      <DialogActions>
        <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
        <LoadingButton
          onClick={handleConfirm}
          loading={loading}
        >
          {t('common:confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FinalisationSection;
